import React, { useState } from 'react';
import { Event, EventRecallDetails, getProductRecall, triggerMassRecall } from '../../../../../model/event';
import { Card } from '../../../../../components/Card/Card';
import { KeyValueTable } from '../../../../../components/KeyValueTable/KeyValueTable';
import { t } from '../../../../../locales/translate';
import { Button } from '../../../../../components/Button/Button';
import { ControlledModal } from '../../../../../components/Modal/ControlledModal';
import { MassRefundForm } from './Modals/MassRefundForm';
import { useAsync } from '../../../../../model/data';
import { Icon } from '../../../../../components/Icon/Icon';
import { Spinner } from '../../../../../components/Spinner/Spinner';
import { useConfirmation } from '../../../../../components/ConfirmationPrompt/ConfirmationPrompt';
import { formatDateTime } from '../../../../../util/util';
import { handleSuccess } from '../../../../../model/notification';
import { ProductRecall } from '../../../../../model/event';

interface Props {
  event: Event;
}

export const MassRefund = ({ event }: Props): JSX.Element => {
  const [loading] = useAsync(() => getProductRecall(event.id));
  const [displayModal, setDisplayModal] = useState(false);

  async function handleMassRefund(): Promise<void> {
    await triggerMassRecall(event.id);
    handleSuccess(t.model.event.massRefund.refundSuccessful);
  }

  const { confirm } = useConfirmation({
    title: t.model.event.massRefund.triggerMassRefund,
    children: (
      <>
        {t.model.event.massRefund.sendEmailTo}
        <span className="mass-refund__sold-tickets">{` ${t.model.event.massRefund.peopleNum(
          event.salesStats?.sold ?? 0
        )} `}</span>
        {t.model.event.massRefund.withInstructions}
      </>
    ),
    action: handleMassRefund,
  });

  function validRecallObject(recallObject?: ProductRecall): boolean {
    if (!recallObject) {
      return false;
    }
    return (
      !!(recallObject.emailSubjectDe && recallObject.emailTemplateDe) ||
      !!(recallObject.emailSubjectEn && recallObject.emailTemplateEn)
    );
  }

  return (
    <Card
      title={t.model.event.massRefund._name}
      actions={
        <Icon
          name="edit"
          onClick={() => {
            setDisplayModal(true);
          }}
        />
      }
    >
      {loading ? (
        <Spinner color="var(--color-tint)" />
      ) : (
        <>
          <KeyValueTable
            items={[
              {
                key: t.model.event.massRefund.massRefundable,
                value: event.recallDetails?.id ? t.general.yes : t.general.no,
              },
              {
                key: t.model.event.massRefund.autoBookRefunds,
                value: event.recallDetails?.paymentsMassBookable ? t.general.yes : t.general.no,
              },
              {
                key: t.model.event.massRefund.usedTicketsMassRefundable,
                value: event.recallDetails?.usedTicketsMassRefundable ? t.general.yes : t.general.no,
              },
              {
                key: t.model.event.massRefund.refundPaused,
                value: event.recallDetails?.paused ? t.general.yes : t.general.no,
              },
              { key: 'separator' },
              {
                key: t.model.event.massRefund.emailSubjectDe,
                value: event.recallDetails?.emailSubjectDe,
              },
              {
                key: t.model.event.massRefund.emailTemplateDe,
                value: event.recallDetails?.emailTemplateDe ? <Icon name="check" /> : undefined,
              },
              {
                key: t.model.event.massRefund.emailSubjectEn,
                value: event.recallDetails?.emailSubjectEn,
              },
              {
                key: t.model.event.massRefund.emailTemplateEn,
                value: event.recallDetails?.emailTemplateEn ? <Icon name="check" /> : undefined,
              },
              { key: 'separator' },
              {
                key: t.model.event.massRefund.customTitleDe,
                value: event.recallDetails?.titleDe,
              },
              {
                key: t.model.event.massRefund.customTitleEn,
                value: event.recallDetails?.titleEn,
              },
              { key: 'separator' },
              {
                key: t.model.event.massRefund.refundExplanationDe,
                value: event.recallDetails?.textDe,
              },
              {
                key: t.model.event.massRefund.refundExplanationEn,
                value: event.recallDetails?.textEn,
              },
            ]}
          />

          {event.recallDetails?.triggeredAt && (
            <div className="mass-refund__triggered">
              {`${t.model.event.massRefund.refundTriggeredAt}: ${formatDateTime(event.recallDetails?.triggeredAt)}`}
            </div>
          )}
          <Button
            disabled={!validRecallObject(event.recallDetails)}
            className="mass-refund__trigger"
            type="primary"
            title={t.model.event.massRefund.triggerMassRefund}
            onClick={confirm}
          />
        </>
      )}
      <ControlledModal<EventRecallDetails>
        open={displayModal}
        model={{ productId: event.id, ...event.meta, ...event.recallDetails }}
        onClose={() => setDisplayModal(false)}
        title={
          event.recallDetails
            ? t.actions.edit(t.model.event.massRefund._name)
            : t.actions.add(t.model.event.massRefund._name)
        }
        size="small"
      >
        {(controller) => <MassRefundForm controller={controller} />}
      </ControlledModal>
    </Card>
  );
};
